/**
 * Focus restoration after a user-initiated nodes-array rebuild (WCAG 2.4.3,
 * PR #8 review; generalised by ADR B11 / README INV-11).
 *
 * When a keyboard move re-ranks a card, or a per-card collapse/expand swaps
 * the card into a different parent block (ADR B4/B11 — the expanded and
 * collapsed renders put the button pair in different parents, so React
 * unmounts and remounts them), the optimistic rebuild makes React relocate
 * or replace the initiating control's DOM node, and the browser resets
 * document.activeElement to <body> — ejecting the keyboard user to the top
 * of the document. The restore mechanism is a pure DOM function, unit-tested
 * directly; the real-browser end-to-end behavior (activeElement back on the
 * initiating control after the rebuild) is asserted in
 * tests/e2e/map_implementation_plans/implementation-plans.spec.ts (the
 * subsystem's single serial E2E file; see ADR A6/E1).
 * (An isolated jsdom insertBefore does not reproduce the blur, though the
 * full React relocation path can — see the PR #8 review notes.)
 *
 * **`restoreFocus` returns a bare `false` for TWO different misses**
 * (ADR.ImplementationPlansUX2 C6b, fact 9): the card's own node is gone from
 * `container`, or the card is there but no `[data-control]` inside it
 * matches. It does not — and cannot, from inside this pure DOM query —
 * distinguish the two; the caller (`Map3ImplementationPlans`'s restore
 * effect) does that by separately checking whether the card's uuid is still
 * in the current `nodes` array, which is what its four-branch clearing rule
 * (C6b) is built on.
 */

import type { ImplementationPlanColumn } from '@mm-shared/types/Objective1';
import type { PlanCardCollapse } from './planLayout';

export type PendingFocus = {
  objectiveUuid: string;
  /**
   * 'collapse-card' / 'expand-card' (ADR B4/B11): focus returns to the
   * button the user pressed, not to its partner — both buttons are
   * permanently mounted in both renders (B4), so the one that was activated
   * still exists after the transition.
   *
   * The template-literal member (ADR.ImplementationPlansUX2 C2a/C6b) is the
   * key-result checkbox's own `data-control`, unique **per row**
   * (`toggle-delivered:<resultUuid>`) rather than shared like every other
   * member here — `restoreFocus` below takes the FIRST `[data-control]` match
   * in the card, and a shared name would focus row 1 after a row-4 toggle.
   */
  control:
    | 'move-up'
    | 'move-down'
    | 'move-column'
    | 'collapse-card'
    | 'expand-card'
    | `toggle-delivered:${string}`;
  /**
   * The card's column at the moment the record was written
   * (ADR.ImplementationPlansUX2 C6b). Consumed only by the canvas's restore
   * effect, never by `restoreFocus` itself: when the card the user was
   * standing on has vanished from `nodes` entirely (checking the last
   * undelivered key result on a `hideDone` column hides the card that owned
   * the checkbox), focus falls back to that column's own hide-done toggle —
   * `[data-control="toggle-hide-done"][data-column="<fallbackColumn>"]` — the
   * one control that explains where the card went. Present only on the
   * `toggle-delivered:*` record the checkbox writes; every other gesture's
   * card survives its own rebuild by construction, so no fallback applies.
   */
  fallbackColumn?: ImplementationPlanColumn;
};

/**
 * Refocuses the control that initiated a keyboard move, inside the card's
 * (possibly relocated) DOM node. Returns true if focus was restored.
 */
export function restoreFocus(container: ParentNode, pending: PendingFocus): boolean {
  const card = container.querySelector(
    `.react-flow__node[data-id="${pending.objectiveUuid}"]`,
  );
  if (!card) return false;
  const control = card.querySelector<HTMLElement>(`[data-control="${pending.control}"]`);
  if (!control) return false;
  control.focus();
  return true;
}

/**
 * Focuses the CARD's own node wrapper — the element ReactFlow renders with
 * `tabIndex={0}` and the `Objective: … Status: …` accessible name
 * (`Map3ImplementationPlans`'s `ariaLabel`) — rather than a control inside it.
 * Returns true if the wrapper was found.
 *
 * The one caller is the restore effect's branch-2b arm (bead mr-60dv.10): the
 * user's own toggle click removed the ROW while the CARD survived, so there is
 * no control left to return to, but there *is* a focusable, named element
 * exactly where the user was standing. Focusing it announces which objective
 * they are on and what its status is, instead of dropping them on `<body>` at
 * the top of the document.
 *
 * Deliberately NOT used by branch 4 (the two-strike give-up): there the record
 * expired for a reason nobody enumerated, on a rebuild the user often did not
 * cause, and moving focus at that moment is the same species of interruption
 * branch 2's fallback is earned by the card actually vanishing.
 */
export function focusCardNode(container: ParentNode, objectiveUuid: string): boolean {
  const card = container.querySelector<HTMLElement>(
    `.react-flow__node[data-id="${objectiveUuid}"]`,
  );
  if (!card) return false;
  card.focus();
  return true;
}

const TOGGLE_DELIVERED_PREFIX = 'toggle-delivered:';

/**
 * Whether the record's own control can exist in the render the canvas just
 * committed (ADR.ImplementationPlansUX2 C6b, tech-lead follow-up ruling on
 * PR #482 — "branch 2b") — decidable from `viewCards` alone, for the ONE
 * control shape whose two disappearance causes are both visible there:
 * `toggle-delivered:<uuid>` is gone when the row it names is no longer in
 * `card.keyResults` (a collaborator deleted it), or when the card is no
 * longer `expanded` (B5's block exclusivity removed the whole key-result
 * list). Every other control returns `true` unconditionally — deliberately
 * **not** generalised to `move-up`/`move-down`/`move-column`: those render
 * under the card component's own `canEdit && !isEditing`, and `isEditing`
 * is local state inside `ObjectiveStickyNoteNode`, invisible to the canvas.
 * A canvas-side copy of the child's render conditions would drift. Policy,
 * folded inside `decideFocusRestore` rather than exported — the canvas
 * effect has no business knowing this predicate exists.
 */
function rowSurvives(
  cardCollapse: PlanCardCollapse | undefined,
  keyResultUuids: readonly string[] | undefined,
  control: PendingFocus['control'],
): boolean {
  if (!control.startsWith(TOGGLE_DELIVERED_PREFIX)) return true;
  const resultUuid = control.slice(TOGGLE_DELIVERED_PREFIX.length);
  return cardCollapse === 'expanded' && (keyResultUuids ?? []).some((uuid) => uuid === resultUuid);
}

/**
 * The restore effect's four outcomes, as data (ADR.ImplementationPlansUX2
 * C6b, tech-lead follow-up ruling on PR #482 — corrects the first ruling's
 * "the gate lands attempts against the real post-rebuild DOM" premise: it
 * does not and cannot — `@xyflow/react`'s `StoreUpdater` syncs the `nodes`
 * prop into its internal store from a PASSIVE EFFECT, one commit after the
 * commit that carries it, in every environment, not just jsdom).
 */
export type FocusRestoreDecision =
  | { kind: 'restore' } // branch 1
  | { kind: 'fallback'; column: ImplementationPlanColumn | undefined } // branch 2
  /**
   * Branches 2b and 4. Both clear the record and the counter; they differ in
   * **where focus lands**, which is why the outcome carries it as data rather
   * than splitting into a fifth `kind` (bead mr-60dv.10):
   *
   * - `'card'` (branch 2b) — the card survived and the user's own gesture
   *   destroyed the row, so focus moves to the card's node wrapper.
   * - `'none'` (branch 4) — the two-strike bound expired for an unenumerated
   *   reason on a rebuild the user may not have caused; focus moves nowhere.
   */
  | { kind: 'clear'; focus: 'card' | 'none' }
  | { kind: 'retry' }; // branch 3

/**
 * Decides the restore effect's outcome from data the canvas already has —
 * `viewCards` and the one true DOM fact the effect learned from
 * `restoreFocus` — never from `nodes`/the DOM directly. Pure: no refs, no
 * DOM, no side effect, so it is unit-testable exhaustively (unit 47f)
 * without a render, a jsdom timing race, or a real browser.
 *
 * `controlFound` is the ONLY input this function cannot derive for itself —
 * it is `restoreFocus`'s own return value, injected by the caller. Callers
 * MUST NOT invoke `restoreFocus` (its one side effect is calling `.focus()`)
 * before first confirming the decision actually depends on it: the
 * `!cardPresent` and `!rowSurvives` arms below return the same `kind`
 * regardless of `controlFound`, and per the ADR, branches 2 and 2b are
 * decided from `viewCards` **before the DOM is consulted at all** — calling
 * `restoreFocus` anyway risks focusing a control ReactFlow's own
 * one-commit-later store sync is about to remove.
 *
 * `misses` is the two-strike bound (ADR C6b): `0` on the first attempt for
 * a record, `1` after one confirmed miss. It is not this function's job to
 * increment or reset it — the caller updates its ref from the `kind`
 * returned (`retry` leaves it at `1`; every other `kind` clears it to `0`
 * along with the record itself, whether by resolving, falling back, or
 * giving up).
 *
 * The function decides WHERE focus goes as well as whether the record clears
 * — `clear` carries `focus: 'card' | 'none'` (mr-60dv.10). It still performs
 * no focus move itself; that stays the caller's single side-effecting step.
 */
export function decideFocusRestore(input: {
  /** Whether the record's card is present in `viewCards` (never `nodes`/DOM). */
  cardPresent: boolean;
  cardCollapse: PlanCardCollapse | undefined;
  keyResultUuids: readonly string[] | undefined;
  control: PendingFocus['control'];
  fallbackColumn: ImplementationPlanColumn | undefined;
  /** `restoreFocus`'s own return value — the one DOM fact, injected. */
  controlFound: boolean;
  misses: 0 | 1;
}): FocusRestoreDecision {
  if (!input.cardPresent) {
    // Branch 2: the card itself is gone.
    return { kind: 'fallback', column: input.fallbackColumn };
  }
  if (!rowSurvives(input.cardCollapse, input.keyResultUuids, input.control)) {
    // Branch 2b: the card survives but its own control cannot possibly
    // exist in this render. Focus lands on the card's own node wrapper
    // (mr-60dv.10) — the user's gesture destroyed the row, the card is still
    // exactly where they left it, and the wrapper is focusable and named.
    return { kind: 'clear', focus: 'card' };
  }
  if (input.controlFound) {
    // Branch 1 (shipped).
    return { kind: 'restore' };
  }
  if (input.misses === 0) {
    // Branch 3: first consecutive miss — leave the record armed.
    return { kind: 'retry' };
  }
  // Branch 4: second consecutive miss — clear without moving focus.
  return { kind: 'clear', focus: 'none' };
}
